import { BottomSheet } from '../premium';
import type { MemberDaySnapshot } from '../../types/circles';

export function MemberDaySheet({
  open,
  onClose,
  snapshot,
  loading,
  dateLabel = 'Today',
}: {
  open: boolean;
  onClose: () => void;
  snapshot: MemberDaySnapshot | null;
  loading: boolean;
  dateLabel?: string;
}) {
  return (
    <BottomSheet open={open} onClose={onClose} title={snapshot?.name ?? 'Member'}>
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 rounded-2xl bg-white/5" />
          <div className="h-24 rounded-2xl bg-white/5" />
        </div>
      ) : !snapshot ? (
        <p className="text-sm text-zinc-400">Could not load this member&apos;s day.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-zinc-500">{dateLabel} · privacy-respecting view</p>

          {snapshot.momentumScore != null ? (
            <div className="rounded-2xl border border-violet-500/25 bg-violet-500/10 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-violet-300">Momentum</div>
              <div className="mt-1 font-display text-3xl font-bold text-white">{snapshot.momentumScore}</div>
              {snapshot.positiveSummary ? (
                <p className="mt-2 text-sm text-zinc-300">{snapshot.positiveSummary}</p>
              ) : null}
            </div>
          ) : null}

          {snapshot.streak != null && snapshot.streak > 0 ? (
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-200">
              {snapshot.streak}-day workout streak
            </div>
          ) : null}

          {snapshot.workouts != null ? (
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-sky-300">Workouts</div>
              {snapshot.workouts.length === 0 ? (
                <p className="text-sm text-zinc-500">No workout logged this day.</p>
              ) : (
                <ul className="space-y-2">
                  {snapshot.workouts.map((w) => (
                    <li key={w.id} className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5">
                      <div className="font-semibold text-zinc-100">{w.name}</div>
                      {w.durationMinutes != null || w.estimatedCaloriesBurned != null ? (
                        <div className="mt-0.5 text-xs text-zinc-500">
                          {w.durationMinutes ? `${w.durationMinutes} min` : ''}
                          {w.durationMinutes && w.estimatedCaloriesBurned ? ' · ' : ''}
                          {w.estimatedCaloriesBurned ? `${w.estimatedCaloriesBurned} kcal` : ''}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {(snapshot.mealsLogged != null || snapshot.calories != null || snapshot.proteinG != null || snapshot.waterMl != null) ? (
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-300">Nutrition</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {snapshot.mealsLogged != null ? (
                  <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Meals</div>
                    <div className="font-bold text-zinc-100">{snapshot.mealsLogged}</div>
                  </div>
                ) : null}
                {snapshot.calories != null ? (
                  <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Calories</div>
                    <div className="font-bold text-zinc-100">{snapshot.calories}</div>
                  </div>
                ) : null}
                {snapshot.proteinG != null ? (
                  <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Protein</div>
                    <div className="font-bold text-zinc-100">{Math.round(snapshot.proteinG)}g</div>
                  </div>
                ) : null}
                {snapshot.waterMl != null ? (
                  <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Water</div>
                    <div className="font-bold text-zinc-100">{Math.round(snapshot.waterMl / 100) / 10}L</div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {snapshot.sleepHours != null ? (
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-300">Sleep</div>
              <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2 text-sm font-bold text-zinc-100">
                {snapshot.sleepHours}h logged
              </div>
            </div>
          ) : null}

          {snapshot.momentumScore == null &&
          snapshot.workouts == null &&
          snapshot.mealsLogged == null &&
          snapshot.sleepHours == null ? (
            <p className="text-sm text-zinc-500">This member keeps this day&apos;s details private.</p>
          ) : null}
        </div>
      )}
    </BottomSheet>
  );
}
